import React, {useCallback, useRef} from 'react';
import type {NodeLocationInfo} from 'Contexts/NodeCallBackContext';
import {useAnimationFrame} from 'Hooks/useAnimationFrame';
import {getEdgesAndOffsets} from 'utils/getEdgesAndOffsets';

interface Props {
    isLeftHandle?: boolean;
    onClick: () => void;
    xy: () => NodeLocationInfo['actual'];
    locationInfo: () => NodeLocationInfo;
    rootLocationInfo: () => NodeLocationInfo;
}

interface VerticalProps extends Props {
    itemWidth: number;
    edge: 'top' | 'bottom';
}
interface HorizontalProps extends Props {
    itemHeight: number;
    edge: 'left' | 'right';
}

const BORDER_WIDTH = 2;

export const HorizontalHandleToggle: React.FC<HorizontalProps> = ({
    itemHeight,
    edge,
    isLeftHandle,
    xy,
    locationInfo,
    rootLocationInfo,
    onClick,
    ...props
}) => {
    const ref = useRef<null | HTMLDivElement>(null);

    const styles: React.CSSProperties = {
        position: 'absolute',
        transform: 'translateY(-50%)',
        height: 9,
        width: 9,
        zIndex: 11,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const updateOnFrame = useCallback(() => {
        if (ref.current) {
            const {
                topEdgeOfSelectedNode: top,
                leftEdgeOfSelectedNode: left,
                rightEdgeOfSelectedNode: right,
                offsetTop,
                offsetLeft,
            } = getEdgesAndOffsets(xy, locationInfo, rootLocationInfo);

            const styles = ref.current.style;

            styles.left = `${edge === 'right' ? right + offsetLeft - 3.5 : left + offsetLeft - BORDER_WIDTH - 3.5}px`;
            styles.top = top + offsetTop + itemHeight * 0.5 + 'px';
        }
    }, [edge, itemHeight, locationInfo, rootLocationInfo, xy]);

    useAnimationFrame(updateOnFrame);

    return (
        <div ref={ref} style={styles} onClick={onClick}>
            <svg width={9} height={9} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
                <circle cx={4.5} cy={4.5} r={4.5} fill="#7B74FF" />
            </svg>
        </div>
    );
};

export const VerticalHandleToggle: React.FC<VerticalProps> = ({
    edge,
    itemWidth,
    xy,
    locationInfo,
    rootLocationInfo,
    onClick,
    ...props
}) => {
    const ref = useRef<null | HTMLDivElement>(null);

    const styles: React.CSSProperties = {
        position: 'absolute',
        transform: 'translateX(-50%)',
        width: 9,
        height: 9,
        zIndex: 11,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const updateOnFrame = useCallback(() => {
        if (ref.current) {
            const {
                topEdgeOfSelectedNode: top,
                leftEdgeOfSelectedNode: left,
                bottomEdgeOfSelectedNode: bottom,
                offsetTop,
                offsetLeft,
            } = getEdgesAndOffsets(xy, locationInfo, rootLocationInfo);

            const styles = ref.current.style;

            styles.left = `${left + offsetLeft + itemWidth * 0.5}px`;
            styles.top = (edge === 'bottom' ? bottom + offsetTop - 3.5 : top + offsetTop - BORDER_WIDTH - 3.5) + 'px';
        }
    }, [edge, itemWidth, locationInfo, rootLocationInfo, xy]);

    useAnimationFrame(updateOnFrame);

    return (
        <div ref={ref} style={styles} onClick={onClick}>
            <svg width={9} height={9} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
                <circle cx={4.5} cy={4.5} r={4.5} fill="#7B74FF" />
            </svg>
        </div>
    );
};
